import { postAlertSendMessage } from "api/alertAPI";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { Assignee } from "api/interfaces/alertInterface.interface";
import { CustomError } from "api/interfaces/commonInterface.interface";
import Input from "components/atoms/input/Input";
import { ModalInput } from "components/atoms/input/modal/ModalInput";
import TextArea from "components/atoms/input/TextArea";
import RBACWrapper from "components/blocks/function/RBACWrapper";
import SmallPopupTemplate from "components/templates/modal/small/SmallPopupTemplate";
import useApiError from "hook/useApiError";
import useStatusMutation from "hook/useStatusMutation";
import { SendEmailModal } from "pages/dashboard/map/sub/infoView/listInfoItem/SendEmailModal";
import { useCallback, useEffect, useRef, useState } from "react";
import { BiMailSend, BiMessageRoundedDots } from "react-icons/bi";
import { useAppSelector } from "redux/hooks";
import { selectStomp } from "redux/stomp/stompSelector";
import { checkEmpty, toTrim } from "utils/functions";

interface Props extends Assignee {
  refetch: () => void;
  placeholder?: string;
  recorderId: string;
  label?: string;
}

export default function SendAssignee({
  assignee,
  assigneeId,
  assigneePhone,
  assigneeMail,
  notifySms,
  refetch,
  placeholder,
  recorderId,
  label,
}: Props) {
  const { userUpdatedId } = useAppSelector(selectStomp);
  const [isSendMessageModal, setIsSendMessageModal] = useState<boolean>(false);
  const [isSendEmailModal, setIsSendEmailModal] = useState<boolean>(false);
  const lastUpdatedAt = useRef<Date>(new Date());
  const { handleErrorNotify, notifySuccess, notifyError, lang } = useApiError();
  const [smsMessage, setSmsMessage] = useState<string>("");

  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const mutationSendAlertMessage = useStatusMutation(postAlertSendMessage, {
    onSuccess: () => {
      notifySuccess(lang.label.common.notify.message.success);
      setIsSendMessageModal(false);
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.common.notify.message.fail);
    },
  });

  const onClickSendSms = useCallback(() => {
    if (!assignee || !assigneePhone || !recorderId) return;
    if (!mutationSendAlertMessage.idle) return;
    if (checkEmpty(smsMessage)) {
      notifyError(lang.label.subscription.sendEmail.empty);
      return;
    }

    mutationSendAlertMessage.mutate({
      accountId: selectedAccount.accountId,
      recorderId: recorderId,
      payload: {
        phoneNumber: assigneePhone as string,
        message: toTrim(smsMessage),
      },
    });
  }, [
    assigneePhone,
    lang.label.subscription.sendEmail.empty,
    mutationSendAlertMessage,
    recorderId,
    selectedAccount.accountId,
    smsMessage,
  ]);

  useEffect(() => {
    if (!assigneeId) return;
    if (!userUpdatedId) return;

    if (
      userUpdatedId.userId === assigneeId &&
      userUpdatedId.updatedAt.getTime() > lastUpdatedAt.current?.getTime()
    ) {
      refetch();
      lastUpdatedAt.current = userUpdatedId.updatedAt;
    }
  }, [userUpdatedId, assigneeId, refetch]);

  return (
    <>
      <div className={`assignee-name ${!assignee ? "no-assignee" : ""}`}>
        {assignee ? `${label ?? ""} ${assignee}` : placeholder}
      </div>

      {assignee && (
        <RBACWrapper requiredPermissions="manageAlertRules">
          <div className="assignee-button-wrapper">
            {assigneePhone !== "" && notifySms && (
              <button
                className="assignee-button"
                onClick={() => setIsSendMessageModal(true)}
                data-testid="alert-view-components-button-set-is-send-message-modal"
              >
                <BiMessageRoundedDots size={20} />
              </button>
            )}

            {assigneeMail !== "" && (
              <button
                className="assignee-button"
                onClick={() => setIsSendEmailModal(true)}
                data-testid="alert-view-components-button-set-is-send-email-modal"
              >
                <BiMailSend size={20} />
              </button>
            )}
          </div>
          {isSendEmailModal && assignee && (
            <SendEmailModal
              assignee={{
                assigneeEmail: assigneeMail,
                assigneeName: assignee,
                assigneePhone: assigneePhone,
                notifySms: notifySms,
              }}
              recorderId={recorderId}
              onModal={() => setIsSendEmailModal(false)}
            />
          )}

          {isSendMessageModal && assignee && assigneePhone && (
            <SmallPopupTemplate
              cancel
              title="Send a Text Message"
              subTitle="Send a message to your technician"
              onClick={() => onClickSendSms()}
              onModal={() => setIsSendMessageModal(false)}
              confirmLabel="Send"
              className="full-width"
              data-testid="alert-view-components-small-popup-template-click-send-sms"
            >
              <ModalInput label="Phone Number" essential>
                <Input
                  placeholder="Please enter a phone number"
                  defaultValue={assigneePhone}
                  disabled={true}
                />
              </ModalInput>
              <ModalInput label="Message" essential>
                <TextArea
                  placeholder="Enter your message here"
                  value={smsMessage}
                  onChange={(e) => setSmsMessage(e.target.value)}
                  data-testid="alert-view-components-textarea-set-sms-message"
                />
              </ModalInput>
            </SmallPopupTemplate>
          )}
        </RBACWrapper>
      )}
    </>
  );
}
